<template>
  <node-view-wrapper ref="viewWrapper">
    <div style="display: flex; align-items: center">
      <template v-if="attachmentUpload">
        <template v-if="imageMode !== 'attachment'">
          <v-progress-circular
            v-if="isUploading"
            indeterminate
            color="primary"
          />
          <attachment-upload-table-item
            v-else-if="error"
            name="Datei ist fehlerhaft"
            :upload-error="error"
            :loading="false"
            :selected="selected"
            :editable="editable"
            @delete="deleteSelf"
            style="cursor: pointer"
          />
          <attachment-item-image-mode
            v-else
            :selected="selected"
            :image-size="imageSize"
            :image-src="fullImage || thumbDataUrl"
            :file-name="name"
            @updateDisplayMode="updateDisplayMode"
            @updateSize="updateSize"
            @fileNameChange="updateName"
            :image-mode="imageMode"
          ></attachment-item-image-mode>
        </template>
        <attachment-upload-table-item
          v-else
          :name="name"
          :file-extension="type"
          :thumb-url="thumbDataUrlIfSupportedImage"
          :upload-error="error"
          :upload-progress="attachmentUpload.uploadProgress"
          :selected="selected"
          :editable="editable"
          :allow-image-mode="isImage"
          :image-mode-blocked-message="imageModeBlockedMessage"
          :show-grab="true"
          :no-renaming="noRenaming"
          @retry="retry"
          @delete="deleteSelf"
          @uploadName="updateName"
          @showAsImage="updateDisplayMode('full-width')"
          style="cursor: pointer"
        />
      </template>
      <attachment-upload-table-item
        v-else-if="error"
        name="Datei nicht gefunden"
        :upload-error="error"
        :loading="false"
        :selected="selected"
        :editable="editable"
        @delete="deleteSelf"
        style="cursor: pointer"
      />
    </div>
    <div v-if="!attachmentUpload && !error">
      <v-progress-circular indeterminate color="primary" />
    </div>
  </node-view-wrapper>
</template>

<script lang="ts">
/**
 * Requires following provides:
 *  - loadAttachmentByFrontendId
 *  - cancelUpload
 *  - retryUpload
 */

import {
  computed,
  defineComponent,
  inject,
  onBeforeUnmount,
  Ref,
  ref,
} from "vue"
import { nodeViewProps, NodeViewWrapper } from "@tiptap/vue-2"
import Button from "components/UI/Button.vue"
import AttachmentUploadTableItem from "features/attachments/AttachmentUploadTableItem.vue"
import { AttachmentMediaUpload } from "features/attachments/attachment.types"
import AttachmentItemImageMode from "components/Wysiwyg/extensions/attachment/attachment-item/AttachmentItemImageMode.vue"
import { AttachmentFunctions } from "features/attachments/attachment.functions"
import {
  getUnsupportedMessageForAttachmentImageMode,
  isMimeTypeSupported,
} from "app/modules/core/supported-image-formats"

export type ImageMode =
  | "full-width"
  | "max-height"
  | "width"
  | "height"
  | "attachment"

export default defineComponent({
  name: "AttachmentItemNodeView",
  components: {
    AttachmentItemImageMode,
    AttachmentUploadTableItem,
    Button,
    NodeViewWrapper,
  },
  props: nodeViewProps,
  setup(props) {
    const editable = ref(true)
    const noRenaming = computed(
      () =>
        !(
          error.value === undefined ||
          error.value === null ||
          error.value === ""
        )
    )

    const viewWrapper = ref<any>(null)

    const frontendId = computed(() => props.node.attrs["data-frontend-id"])
    const storedFile = computed(() => props.node.attrs["data-stored-file"])
    const fileName = computed(() => props.node.attrs["data-file-name"])
    const name = computed(() => attachmentUpload?.value?.name)
    const type = computed(() => attachmentUpload?.value?.type)
    const thumbDataUrl = computed(() => attachmentUpload?.value?.thumbDataUrl)
    const isUploading = computed(() => {
      return (
        attachmentUpload?.value?.uploadProgress !== 100 &&
        attachmentUpload?.value?.uploadProgress !== undefined
      )
    })

    const loadError = ref<string>(undefined)
    const error = computed(
      () =>
        (attachmentUpload?.value?.errors &&
          attachmentUpload?.value?.errors[0]) ||
        loadError.value
    )

    const loadAttachmentByFrontendId: (
      frontendId: string
    ) => Ref<AttachmentMediaUpload> = inject("loadAttachmentByFrontendId")

    const cancel: (frontendId: string) => void = inject("cancelUpload")
    const retry: (frontendId: string) => void = inject("retryUpload")

    const attachmentUpload = loadAttachmentByFrontendId(frontendId.value)

    const inSelection = ref(false)
    // the selected prop only is true if component is clicked, so we need to listen to the text selection range
    const isSelected = () => {
      const selection = props.editor.view.state.selection
      const startPos = props.getPos()
      const endPos = startPos + props.node.nodeSize

      inSelection.value = selection.from <= startPos && selection.to >= endPos
    }

    props.editor.on("selectionUpdate", isSelected)
    onBeforeUnmount(() => props.editor.off("selectionUpdate", isSelected))
    onBeforeUnmount(() => {
      if (
        !storedFile.value &&
        attachmentUpload.value.uploadProgress !== undefined
      ) {
        cancel(frontendId.value)
      }
    })

    const deleteSelf = () => props.deleteNode()

    const imageMode = computed<ImageMode>(
      () => props.node.attrs["data-image-mode"] ?? "attachment"
    )
    const imageSize = computed<number | undefined>(
      () => Number.parseInt(props.node.attrs["data-image-size"]) || undefined
    )

    const fullImage = computed(
      () =>
        attachmentUpload?.value?.fullImageUrl ??
        attachmentUpload?.value?.file?.dataURL
    )

    const updateName = (newName: string) => {
      attachmentUpload.value = {
        ...attachmentUpload.value,
        name: newName,
      }
      props.updateAttributes({ "data-file-name": newName })
    }

    const updateDisplayMode = (mode: ImageMode) =>
      // Remove data attributes
      props.updateAttributes({ "data-image-mode": mode })

    const updateSize = (size: number) =>
      props.updateAttributes({ "data-image-size": size })

    const isImage = computed(() => {
      if (!attachmentUpload.value) return false
      return AttachmentFunctions.isImage(attachmentUpload.value)
    })

    const isImageSupported = computed(() => {
      if (!attachmentUpload.value) return false
      return isMimeTypeSupported(
        attachmentUpload.value.mimeContentType ??
          attachmentUpload.value.type ??
          attachmentUpload.value.file?.type
      )
    })

    const imageModeBlockedMessage = computed(() => {
      if (isImageSupported.value) return undefined
      return getUnsupportedMessageForAttachmentImageMode(
        attachmentUpload.value.mimeContentType ??
          attachmentUpload.value.type ??
          attachmentUpload.value.file?.type
      )
    })

    const thumbDataUrlIfSupportedImage = computed(() => {
      if (!isImageSupported.value) return undefined
      return thumbDataUrl.value
    })

    return {
      frontendId,
      attachmentUpload,
      updateName,
      storedFile,
      fileName,
      deleteSelf,
      retry,
      editable,
      name,
      type,
      thumbDataUrl,
      thumbDataUrlIfSupportedImage,
      error,
      viewWrapper,
      imageSize,
      fullImage,
      updateSize,
      updateDisplayMode,
      imageMode,
      isUploading,
      isImage,
      isImageSupported,
      imageModeBlockedMessage,
      noRenaming,
    }
  },
})
</script>

<style scoped>
.drag-handle {
  flex: 0 0 auto;
  position: relative;
  width: 1rem;
  height: 1rem;
  top: 0.3rem;
  margin-right: 0.5rem;
  cursor: grab;
  background-image: url('data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 16"><path fill-opacity="0.2" d="M4 14c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zM2 6C.9 6 0 6.9 0 8s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6C.9 0 0 .9 0 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" /></svg>');
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
}
</style>
